import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { CurrencyToSign } from '@monorepo/client-types';
import {
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  Typography,
} from '@monorepo/nova-ui';
import { InvoiceDisplayData, ProductType } from '@monorepo/types';
import InvoiceConclusion from './invoice-conclustion';
import InvoiceDetails from './invoice-details';
import InvoiceTableBody from './invoice-table-body';
import InvoiceTableHeader from './invoice-table-header';

interface Props {
  invoiceData: InvoiceDisplayData;
}

const InvoiceTable: FunctionComponent<Props> = ({ invoiceData }) => {
  const { t } = useTranslation(['orders', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const items = invoiceData.purchase.items.map((item) => {
    const metadata = item.metadata as { tradingSystemFee?: number };
    const tradingSystemFee = metadata.tradingSystemFee || 0;

    return {
      productName: item.metadata.productName!,
      originPrice: item.unitPrice,
      tradingSystemFee,
      currency: invoiceData.purchase.paymentData.currency,
      currencyRate: invoiceData.purchase.paymentData.currencyRate,
      productType: item.productType,
      amountInCurrency:
        (item.unitPrice + tradingSystemFee) *
        invoiceData.purchase.paymentData.currencyRate,
    };
  });

  const creditsUsed =
    invoiceData.purchase.paymentData.discountMetadata?.credits?.creditsUsed ??
    0;

  const details: Array<{
    title: string;
    value: number | string;
    prefix?: string;
    precision?: number;
    isText?: boolean;
    isBold?: boolean;
    suffix?: string;
  }> = [];

  if (
    invoiceData.purchase.paymentData.discountMetadata?.convertedTotalDiscount
  ) {
    details.push({
      title: t('common:discount'),
      value:
        invoiceData.purchase.paymentData.discountMetadata
          .convertedTotalDiscount,
      prefix: CurrencyToSign[invoiceData.purchase.paymentData.currency],
      precision: 2,
    });
  }

  if (creditsUsed > 0) {
    const { currencyRate, currency } = invoiceData.purchase.paymentData;
    details.push({
      title: t('common:credits_applied'),
      value: creditsUsed * Number(currencyRate),
      prefix: CurrencyToSign[currency],
      precision: 2,
    });
  }

  const productTypeToLabel = useMemo(() => {
    return {
      [ProductType.Program]: t('orders:new_invoice_table.program'),
      [ProductType.AddOn]: t('orders:new_invoice_table.add_on'),
    } as Record<ProductType, string>;
  }, [items[0]?.productType, t]);

  return (
    <TableContainer>
      {isMobile && (
        <Stack gap={1} marginBottom={2}>
          <Typography variant="caption" color="text.secondary" fontWeight={400}>
            {productTypeToLabel[items[0]?.productType]}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {items[0].productName}
          </Typography>
        </Stack>
      )}
      <Table
        size="small"
        sx={{
          '& .MuiTableCell-root': { px: 3 },
          '& .MuiTableCell-root:first-of-type': { paddingLeft: 0 },
          '& .MuiTableCell-root:last-of-type': { paddingRight: 0 },
        }}
      >
        <TableHead>
          <InvoiceTableHeader
            isMobile={isMobile}
            productLabel={productTypeToLabel[items[0]?.productType]}
          />
        </TableHead>
        <TableBody>
          <InvoiceTableBody items={items} isMobile={isMobile} />
        </TableBody>
        <TableFooter>
          <InvoiceDetails details={details} isMobile={isMobile} />
          <InvoiceConclusion
            amountInCurrency={invoiceData.purchase.paymentData.convertedPrice}
            currency={invoiceData.purchase.paymentData.currency}
            isMobile={isMobile}
          />
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default InvoiceTable;
